package org.example.project.enginekartei

import androidx.compose.runtime.Composable
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.RollenService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File

private const val PROFILE_CARD_BORDER_COLOR = "rgba(255,255,255,0.34)"
private const val KARTEI_CONTROL_BORDER = "1px solid rgba(255,255,255,0.18)"
private const val KARTEI_CONTROL_SHADOW = "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)"

// -- Privates StyleSheet – ersetzt alle inline color/cursor/border Verstöße ---

private object KarteiStyles : StyleSheet() {

    /** Kaskadiert color: white auf alle Kind-Elemente des Root-Wrappers. */
    val rootWrapper by style {
        color(Color.white)
    }

    val rightTitle by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val sectionTitle by style {
        fontSize(24.px)
        fontWeight("700")
        lineHeight("1")
        color(Color.white)
        textAlign("center")
        paddingBottom(60.px)
    }

    /** Textbasierte Seitennavigation wie Quick-Navi. */
    val tabBase by style {
        property("appearance", "none")
        property("-webkit-appearance", "none")
        boxSizing("border-box")
        fontSize(15.px)
        padding(0.px)
        cursor("pointer")
        property("transition", "color 120ms ease, border-color 120ms ease")
        property("line-height", "1")
        backgroundColor(Color.transparent)
        color(Color("#8a8f9a"))
        border(0.px)
        property("border-bottom", "2px solid transparent")
        borderRadius(0.px)
        fontWeight("500")
        property("white-space", "nowrap")
        flexShrink(0)
    }

    val tabActive by style {
        color(Color.white)
        fontWeight("600")
        property("border-bottom", "2px solid white")
        paddingBottom(6.px)
    }

    val tabInactive by style {
        color(Color("#6b7280"))
        paddingBottom(6.px)
        hover { color(Color.white) }
    }

    val tabDivider by style {
        color(Color("#6b7280"))
        fontSize(15.px)
        padding(0.px, 2.px, 6.px, 2.px)
        property("user-select", "none")
    }

    /** Gruppen-Titel in einer Feldgruppe. */
    val groupTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(15.px)
        property("letter-spacing", "0.02em")
    }

    /** Label-Spalte in InlineInput / Checkbox / Dropdown. */
    val labelText by style {
        width(138.px)
        property("min-width", "138px")
        color(Color("#8a8f9a"))
        fontSize(14.px)
    }

    /** Wert-Anzeige (read-only Span). */
    val valueText by style {
        width(100.percent)
        color(Color.white)
        fontSize(17.px)
        padding(0.px, 0.px, 6.px, 0.px)
        boxSizing("border-box")
    }

    /** Unterstrich-Träger rechts neben dem Label. */
    val underlineField by style {
        property("flex", "1 1 auto")
        minWidth(0.px)
        minHeight(36.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        property("border-bottom", "1px solid rgba(255,255,255,0.24)")
        padding(0.px)
        boxSizing("border-box")
    }

    val underlineInput by style {
        width(100.percent)
        height(36.px)
        padding(0.px, 0.px, 6.px, 0.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        border(0.px)
        property("outline", "none")
        boxSizing("border-box")
    }

    val underlineCheckboxValue by style {
        property("margin-left", "auto")
        minWidth(32.px)
        minHeight(36.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        fontSize(20.px)
        property("user-select", "none")
    }

    /** Cursors */
    val cursorPointer  by style { cursor("pointer") }
    val cursorDefault  by style { cursor("default") }

    /** Firmen-Suche Dropdown */
    val firmenDropdownWrap by style {
        position(Position.Relative)
    }
    val firmenDropdownList by style {
        position(Position.Absolute)
        top(100.percent)
        left(0.px)
        right(0.px)
        property("z-index", "999")
        backgroundColor(Color("#1a1f2e"))
        borderRadius(10.px)
        property("border", "1px solid rgba(255,255,255,0.18)")
        property("box-shadow", "0 4px 16px rgba(0,0,0,0.4)")
        property("overflow", "hidden")
    }
    val firmenDropdownItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        property("justify-content", "space-between")
        gap(10.px)
        padding(10.px, 14.px)
        cursor("pointer")
        hover { backgroundColor(Color("rgba(255,255,255,0.06)")) }
    }
    val firmenDropdownItemName by style {
        color(Color.white)
        fontSize(14.px)
    }
    val firmenDropdownItemId by style {
        color(Color("#6b7280"))
        fontSize(12.px)
    }
}

typealias EngineKarteiFieldGroup = All

fun EngineKarteiFieldGroup(
    key: String,
    title: String,
    fields: List<EngineKarteiField>
): All = All(
    key = key,
    bezeichnung = title,
    module = fields
)

private val EngineKarteiFieldGroup.title: String
    get() = bezeichnung

private val EngineKarteiFieldGroup.fields: List<EngineKarteiField>
    get() = module

@Composable
fun EngineKarteiRootLayout(
    title: String = "",
    content: @Composable () -> Unit
) {
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    Style(KarteiStyles)
    Div({
        classes(KarteiStyles.rootWrapper)
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.FlexStart)
            padding(20.px)
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            style {
                width(1240.px)
                maxHeight(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
            }
        }) {
            if (title.isNotBlank() && internalTitleVisible) {
                Div({ classes(KarteiStyles.rightTitle) }) {
                    Span({ classes(KarteiStyles.sectionTitle) }) { Text(title) }
                }
            }
            content()
        }
    }
}

@Composable
fun EngineKarteiFooter(
    showSave: Boolean,
    saveEnabled: Boolean,
    saving: Boolean,
    onSave: () -> Unit,
    saveLabel: String = "Speichern"
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.FlexEnd)
            alignItems(AlignItems.Center)
            gap(14.px)
            property("flex-wrap", "wrap")
            minHeight(42.px)
        }
    }) {
        if (showSave) {
            LumaticaButton(
                label = if (saving) "$saveLabel..." else saveLabel,
                onClick = { onSave() },
                variant = LumaticaButtonVariant.Action,
                enabled = saveEnabled,
                heightPx = 42,
                paddingH = 18
            )
        } else {
            Div({
                style {
                    height(42.px)
                    width(120.px)
                    property("visibility", "hidden")
                    property("pointer-events", "none")
                }
            })
        }
    }
}

@Composable
fun EngineKarteiDeck(
    pages: List<All>,
    activePageId: String,
    onPageChange: (String) -> Unit,
    screenKey: String = "",
    content: @Composable () -> Unit
) {
    val visiblePages = if (screenKey.isBlank()) pages
    else pages.filter { page -> RollenService.isPageVisible(screenKey, page.id) }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            borderRadius(22.px)
            backgroundColor(Color("rgba(255,255,255,0.03)"))
            property("border", "2px solid $PROFILE_CARD_BORDER_COLOR")
            property("backdrop-filter", "blur(14px)")
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                padding(18.px)
                property("box-sizing", "border-box")
            }
        }) {
            content()
        }
    }
}

@Composable
fun EngineKarteiPageNav(
    pages: List<All>,
    activePageId: String,
    onPageChange: (String) -> Unit,
    screenKey: String = ""
) {
    val visiblePages = if (screenKey.isBlank()) pages
    else pages.filter { page -> RollenService.isPageVisible(screenKey, page.id) }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            flexWrap(FlexWrap.Nowrap)
            justifyContent(JustifyContent.Center)
            property("overflow-x", "auto")
            property("overflow-y", "visible")
            property("white-space", "nowrap")
            width(100.percent)
            property("scrollbar-width", "thin")
        }
    }) {
        visiblePages.forEachIndexed { index, tabPage ->
            val isActive = tabPage.id == activePageId
            val tabLabel = tabPage.bezeichnung.trim().ifBlank { tabPage.id }
            Button(attrs = {
                classes(KarteiStyles.tabBase, if (isActive) KarteiStyles.tabActive else KarteiStyles.tabInactive)
                onClick { onPageChange(tabPage.id) }
            }) { Text(tabLabel) }
            if (index < visiblePages.lastIndex) {
                Span({ classes(KarteiStyles.tabDivider) }) { Text("|") }
            }
        }
    }
}

@Composable
fun EngineKarteiPageContent(
    page: All,
    stickyFields: List<EngineKarteiField>,
    fieldGroups: List<EngineKarteiFieldGroup>,
    metaGroup: EngineKarteiFieldGroup? = null,
    showPhoto: Boolean = true,
    imageBlock: @Composable () -> Unit = {},
    pageOverrideContent: (@Composable () -> Unit)? = null,
    fieldRenderer: @Composable (fieldSpec: EngineKarteiField, readonly: Boolean) -> Unit
) {
    val hasPhotoColumn = showPhoto && page.aktiv
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", if (hasPhotoColumn) "240px minmax(0, 1fr)" else "1fr")
            property("align-items", "stretch")
            minHeight(420.px)
            height(100.percent)
            property("box-sizing", "border-box")
        }
    }) {
        if (hasPhotoColumn) {
            EngineKarteiBlocks(
                stickyFields = stickyFields,
                fieldGroups = fieldGroups,
                metaGroup = metaGroup,
                imageBlock = imageBlock,
                pageOverrideContent = pageOverrideContent,
                fieldRenderer = fieldRenderer
            )
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    minHeight(420.px)
                    gap(14.px)
                    padding(18.px)
                    property("border-top-right-radius", "20px")
                    property("border-bottom-right-radius", "20px")
                    property("border-top-left-radius", "0")
                    property("border-bottom-left-radius", "0")
                    backgroundColor(Color("rgba(255,255,255,0.03)"))
                    property("min-width", "0")
                    height(100.percent)
                    property("box-sizing", "border-box")
                }
            }) {
                if (stickyFields.isNotEmpty()) {
                    EngineKarteiAnchorRow(stickyFields, fieldRenderer)
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(14.px)
                        property("margin-top", if (stickyFields.isNotEmpty()) "52px" else "0")
                    }
                }) {
                    fieldGroups.forEach { group ->
                        EngineKarteiFieldGroupCard(group, fieldRenderer)
                    }
                    if (metaGroup != null) {
                        EngineKarteiFieldGroupCard(metaGroup, fieldRenderer, readonly = true)
                    }
                }
                if (pageOverrideContent != null) {
                    pageOverrideContent()
                }
            }
        }
    }
}

@Composable
private fun EngineKarteiBlocks(
    stickyFields: List<EngineKarteiField>,
    fieldGroups: List<EngineKarteiFieldGroup>,
    metaGroup: EngineKarteiFieldGroup?,
    imageBlock: @Composable () -> Unit,
    pageOverrideContent: (@Composable () -> Unit)?,
    fieldRenderer: @Composable (fieldSpec: EngineKarteiField, readonly: Boolean) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("border-right", "1px solid rgba(255,255,255,0.07)")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            borderRadius(22.px)
            property("border-top-right-radius", "0")
            property("border-bottom-right-radius", "0")
            minHeight(420.px)
            height(100.percent)
            padding(22.px)
        }
    }) {
        imageBlock()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(14.px)
            padding(18.px)
            property("border-top-right-radius", "20px")
            property("border-bottom-right-radius", "20px")
            property("border-top-left-radius", "0")
            property("border-bottom-left-radius", "0")
            backgroundColor(Color("rgba(255,255,255,0.03)"))
            minHeight(420.px)
            height(100.percent)
            property("min-width", "0")
            property("justify-content", "flex-start")
            property("box-sizing", "border-box")
        }
    }) {
        if (stickyFields.isNotEmpty()) {
            EngineKarteiAnchorRow(stickyFields, fieldRenderer)
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                minHeight(0.px)
                property("flex", "1 1 auto")
                property("margin-top", if (stickyFields.isNotEmpty()) "52px" else "0")
            }
        }) {
            fieldGroups.forEach { group ->
                EngineKarteiFieldGroupCard(group, fieldRenderer)
            }
            if (metaGroup != null) {
                EngineKarteiFieldGroupCard(metaGroup, fieldRenderer, readonly = true)
            }
        }
        if (pageOverrideContent != null) {
            pageOverrideContent()
        }
    }
}

@Composable
fun EngineKarteiAnchorRow(
    boundAnchorFields: List<EngineKarteiField>,
    fieldRenderer: @Composable (fieldSpec: EngineKarteiField, readonly: Boolean) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr")
            gap(8.px, 10.px)
        }
    }) {
        boundAnchorFields.forEach { field ->
            fieldRenderer(field, false)
        }
    }
}

@Composable
private fun EngineKarteiFieldGroupCard(
    group: EngineKarteiFieldGroup,
    fieldRenderer: @Composable (fieldSpec: EngineKarteiField, readonly: Boolean) -> Unit,
    readonly: Boolean = false
) {
    if (group.fields.isEmpty()) return

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            padding(0.px)
            property("box-sizing", "border-box")
        }
    }) {
        if (group.title.isNotBlank()) {
            H4({ classes(KarteiStyles.groupTitle) }) {
                Text(group.title)
            }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "1fr 1fr")
                gap(8.px, 10.px)
            }
        }) {
            group.fields.forEach { field ->
                fieldRenderer(field, readonly)
            }
        }
    }
}

@Composable
fun EngineKarteiInlineInput(
    label: String,
    value: String,
    editing: Boolean,
    placeholderText: String = "",
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(14.px)
            padding(8.px, 8.px)
            property("box-sizing", "border-box")
        }
    }) {
        Span({ classes(KarteiStyles.labelText) }) { Text(label) }
        Div({ classes(KarteiStyles.underlineField) }) {
            if (editing) {
                Input(type = InputType.Text, attrs = {
                    value(value)
                    if (placeholderText.isNotBlank()) placeholder(placeholderText)
                    classes(KarteiStyles.underlineInput)
                    onInput { onChange(it.value) }
                })
            } else {
                Span({ classes(KarteiStyles.valueText) }) { Text(value.ifBlank { "-" }) }
            }
        }
    }
}

private fun firmenFillMap(firma: org.example.project.utils.All): Map<String, String> = mapOf(
    "firma" to firma.firma,
    "firmenId"   to firma.firmenId.ifBlank { firma.id },
    "strasse"    to firma.strasse,
    "hausnummer" to firma.hausnummer,
    "plz"        to firma.plz,
    "ort"        to firma.ort,
    "land"       to firma.land,
)

@Composable
fun EngineKarteiFirmenSuchField(
    label: String,
    value: String,
    firmen: List<org.example.project.utils.All>,
    searchField: (org.example.project.utils.All) -> String = { it.firma },
    onValue: (String) -> Unit,
    onMultiValue: (Map<String, String>) -> Unit
) {
    val query = value.lowercase()
    val matches = androidx.compose.runtime.remember(query, firmen) {
        if (query.length < 2) emptyList()
        else firmen.filter { searchField(it).lowercase().contains(query) }.take(6)
    }
    Div({ classes(KarteiStyles.firmenDropdownWrap) }) {
        EngineKarteiInlineInput(label = label, value = value, editing = true) { onValue(it) }
        if (matches.isNotEmpty()) {
            Div({ classes(KarteiStyles.firmenDropdownList) }) {
                matches.forEach { firma ->
                    val fid = firma.firmenId.ifBlank { firma.id }
                    Div({
                        classes(KarteiStyles.firmenDropdownItem)
                        onClick { onMultiValue(firmenFillMap(firma)) }
                    }) {
                        Span({ classes(KarteiStyles.firmenDropdownItemName) }) { Text(firma.firma) }
                        Span({ classes(KarteiStyles.firmenDropdownItemId) }) { Text(fid) }
                    }
                }
            }
        }
    }
}

@Composable
fun EngineKarteiCheckbox(
    label: String,
    checked: Boolean,
    editing: Boolean,
    onChange: (Boolean) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(14.px)
            padding(8.px, 8.px)
        }
    }) {
        Span({ classes(KarteiStyles.labelText) }) { Text(label) }

        Div({ classes(KarteiStyles.underlineField) }) {
            Span({
                classes(
                    KarteiStyles.underlineCheckboxValue,
                    if (editing) KarteiStyles.cursorPointer else KarteiStyles.cursorDefault
                )
                style {
                    property("opacity", if (editing) "1" else "0.5")
                }
                if (editing) onClick { onChange(!checked) }
            }) {
                Text(if (checked) "\u2705" else "\u2B1C")
            }
        }
    }
}

@Composable
fun EngineKarteiDropdown(
    label: String,
    value: String,
    options: List<String>,
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(14.px)
            padding(8.px, 8.px)
            property("box-sizing", "border-box")
        }
    }) {
        Span({ classes(KarteiStyles.labelText) }) { Text(label) }

        AppDropdown(
            options       = options.map { All(key = it, label = it) },
            selectedValue = value.ifBlank { null },
            onChange      = onChange,
            placeholder   = "Auswahl",
            appearance    = AppDropdownAppearance.Underline,
            attrs = {
                style {
                    width(100.percent)
                    height(36.px)
                    padding(0.px, 0.px, 6.px, 0.px)
                }
            }
        )
    }
}

@Composable
fun EngineKarteiImageView(
    imageSrc: String,
    fallbackSrc: String,
    editing: Boolean,
    disabled: Boolean,
    statusLabel: String,
    onStatusChange: (String) -> Unit,
    onImageChange: (String) -> Unit,
    onLoadGalleryImages: (suspend () -> List<All>)? = null,
    onUploadGalleryImage: (suspend (File) -> All?)? = null,
    onDeleteGalleryImage: (suspend (All) -> Boolean)? = null,
    onEditorVisibilityChange: (Boolean) -> Unit = {}
) {
    val shownSrc = imageSrc.ifBlank { fallbackSrc }
    Div({
        style {
            property("flex", "1")
            property("min-height", "320px")
            width(100.percent)
            backgroundColor(Color("rgba(255,255,255,0.015)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("overflow", "hidden")
            borderRadius(12.px)
        }
    }) {
        Img(src = shownSrc, attrs = {
            style {
                width(100.percent)
                height(100.percent)
                property("object-fit", if (shownSrc == fallbackSrc) "contain" else "cover")
                padding(if (shownSrc == fallbackSrc) 40.px else 0.px)
                opacity(if (shownSrc == fallbackSrc) 0.5 else 1)
                property("box-sizing", "border-box")
            }
        })
    }
}








